import { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
    $getSelection,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    FORMAT_TEXT_COMMAND,
    REDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    UNDO_COMMAND,
} from 'lexical';

import { useTranslation } from '@packlink/translation-provider';
import { IconSize, IconTooltip } from '@shipengine/giger';
import { IconNames } from '@shipengine/giger-theme';
import { getToolbarGroupStyles, getToolbarItemStyles, getToolbarStyles } from './ToolbarStyles';

export function Toolbar(): JSX.Element {
    const { t } = useTranslation();
    const [editor] = useLexicalComposerContext();

    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
        }
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $updateToolbar();
                    return false;
                },
                1,
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                1,
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                1,
            ),
        );
    }, [$updateToolbar, editor]);

    return (
        <div css={getToolbarStyles}>
            <div css={getToolbarGroupStyles}>
                <button
                    disabled={!canUndo}
                    onClick={() => {
                        editor.dispatchCommand(UNDO_COMMAND, undefined);
                    }}
                    css={getToolbarItemStyles()}
                    type="button"
                    aria-label={t('text-editor.toolbar.undo')}
                >
                    <IconTooltip icon={IconNames.UNDO} size={IconSize.SIZE_MEDIUM} placement="top">
                        {t('text-editor.toolbar.undo')}
                    </IconTooltip>
                </button>

                <button
                    disabled={!canRedo}
                    onClick={() => {
                        editor.dispatchCommand(REDO_COMMAND, undefined);
                    }}
                    css={getToolbarItemStyles()}
                    type="button"
                    aria-label={t('text-editor.toolbar.redo')}
                >
                    <IconTooltip icon={IconNames.REDO} size={IconSize.SIZE_MEDIUM} placement="top">
                        {t('text-editor.toolbar.redo')}
                    </IconTooltip>
                </button>
            </div>

            <div css={getToolbarGroupStyles}>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                    }}
                    css={getToolbarItemStyles(isBold)}
                    type="button"
                    aria-label={t('text-editor.toolbar.bold')}
                >
                    <IconTooltip icon={IconNames.TEXT_BOLD} size={IconSize.SIZE_MEDIUM} placement="top">
                        {t('text-editor.toolbar.bold')}
                    </IconTooltip>
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                    }}
                    css={getToolbarItemStyles(isItalic)}
                    type="button"
                    aria-label={t('text-editor.toolbar.italic')}
                >
                    <IconTooltip icon={IconNames.TEXT_ITALIC} size={IconSize.SIZE_MEDIUM} placement="top">
                        {t('text-editor.toolbar.italic')}
                    </IconTooltip>
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                    }}
                    css={getToolbarItemStyles(isUnderline)}
                    type="button"
                    aria-label={t('text-editor.toolbar.underline')}
                >
                    <IconTooltip icon={IconNames.TEXT_UNDERLINE} size={IconSize.SIZE_MEDIUM} placement="top">
                        {t('text-editor.toolbar.underline')}
                    </IconTooltip>
                </button>
            </div>
        </div>
    );
}
